import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { BaseQueryParams } from '../models/BaseQueryParams';
import { BaseRecord } from '../models/BaseRecord';
import { PagedResponse } from '../models/PagedResponse';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseService<T extends BaseRecord> {
  public loading$: Observable<boolean>;
  public data$: Observable<T[]>;
  public dataCount$: Observable<number>;
  protected currentParameters: BaseQueryParams;
  protected readonly baseApiUrl: string;
  protected dataSubject: BehaviorSubject<T[]>;
  protected loadingSubject: BehaviorSubject<boolean>;
  private dataCountSubject: BehaviorSubject<number>;
  private data: T[];

  protected constructor(
    protected httpClient: HttpClient,
    baseApiUrl: string,
    baseQueryParams?: BaseQueryParams,
  ) {
    this.data = [];
    this.dataSubject = new BehaviorSubject<T[]>([]);
    this.data$ = this.dataSubject.asObservable();
    this.currentParameters =
      baseQueryParams ?? new BaseQueryParams('', '', 0, 100, false, true);
    this.baseApiUrl = baseApiUrl;
    this.loadingSubject = new BehaviorSubject<boolean>(false);
    this.loading$ = this.loadingSubject.asObservable();
    this.dataCountSubject = new BehaviorSubject<number>(0);
    this.dataCount$ = this.dataCountSubject.asObservable();
  }

  public get queryParameters(): BaseQueryParams {
    return this.currentParameters;
  }

  public getById(id: number): Observable<HttpResponse<T>> {
    return this.httpClient.get<T>(`${this.baseApiUrl}/${id}`, {
      observe: 'response',
    });
  }

  public get(
    parameters?: BaseQueryParams,
  ): Observable<HttpResponse<PagedResponse<T>>> {
    if (parameters) {
      this.currentParameters.updateByObject(parameters);
    }
    const params = new HttpParams()
      .set('filter', this.currentParameters.filter)
      .set('sort', this.currentParameters.sort)
      .set('pageSize', this.currentParameters.pageSize)
      .set('pageIndex', this.currentParameters.pageIndex)
      .set('descending', this.currentParameters.descending)
      .set('expand', this.currentParameters.expand);
    this.loadingSubject.next(true);
    return this.httpClient
      .get<PagedResponse<T>>(this.baseApiUrl, { params, observe: 'response' })
      .pipe(
        tap((response) => {
          if (response.ok && response.body) {
            this.data = response.body.items;
            this.dataSubject.next(this.data);
            this.dataCountSubject.next(response.body.count);
            this.loadingSubject.next(false);
          }
        }),
      );
  }

  public create(obj: T): Observable<HttpResponse<T>> {
    return this.httpClient
      .post<T>(this.baseApiUrl, obj, { observe: 'response' })
      .pipe(
        tap((response) => {
          if (response.ok && response.body) {
            this.data.push(response.body as T);

            this.dataSubject.next(this.data);
          }
        }),
      );
  }

  public update(obj: T): Observable<HttpResponse<T>> {
    return this.httpClient
      .put<T>(this.baseApiUrl, obj, { observe: 'response' })
      .pipe(
        tap((response: HttpResponse<T>) => {
          if (response.ok && response.body) {
            const idx = this.data.indexOf(this.data.find((x) => x) as any);
            this.data[idx] = response.body as T;
            this.dataSubject.next(this.data);
          }
        }),
      );
  }

  public delete(obj: T): Observable<HttpResponse<any>> {
    return this.httpClient
      .delete(`${this.baseApiUrl}/${obj.id}`, { observe: 'response' })
      .pipe(
        tap((response: HttpResponse<any>) => {
          if (response.ok && response.body) {
            const idx = this.data.indexOf(obj);
            this.data.splice(idx, 1);
            this.dataSubject.next(this.data);
          }
        }),
      );
  }

  public clearSearchCriteria(): void {
    this.currentParameters = new BaseQueryParams();
  }

  public handleLoadingSubject(value: boolean): void {
    this.loadingSubject.next(value);
  }
}
